/**
 * useOnboardingGates - Unified hook that runs ALL onboarding + pending checks in parallel
 * Replaces sequential: OnboardingGate → PendingRatingsGate → PendingConfirmationGate
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRole } from '@/contexts/RoleContext';
import { useCurrentProfile } from '@/hooks/useCurrentProfile';
import { useCurrentSellerStatus } from '@/hooks/useCurrentSellerStatus';
import { checkWelcomeFromCache, acceptWelcomeOnboarding } from '@/services/welcomeOnboarding';
import { checkStudentLostFromCache, acknowledgeStudentLost } from '@/services/studentLost';
import { checkSellerOnboardingFromCache, acceptSellerOnboarding } from '@/services/sellerOnboarding';
import { fetchPendingRatingsRPC, type PendingRating } from '@/services/pendingRatings';
import { fetchPendingConfirmations, type PendingConfirmation } from '@/services/pendingConfirmations';
import { logActivity } from '@/services/activityLog';
import { useQueryClient } from '@tanstack/react-query';
import { useOnboardingConfig } from '@/hooks/useOnboardingConfig';
import { fetchOnboardingConfig } from '@/services/onboardingConfig';
import { useOnboardingEnabled } from '@/hooks/useOnboardingEnabled';
import { toast } from 'sonner';

export type OnboardingModal = 'none' | 'welcome' | 'student-lost' | 'seller' | 'pending-rating' | 'pending-confirmation';

interface SellerPlanData {
  planLabel: string;
  creditsAmount: number;
  boostAmount: number;
  validityMonths: number;
}

interface OnboardingGatesResult {
  loading: boolean;
  activeModal: OnboardingModal;
  sellerPlanData: SellerPlanData;
  isSubmitting: boolean;
  // Onboarding handlers
  handleAcceptWelcome: () => Promise<void>;
  handleAcknowledgeStudentLost: () => Promise<void>;
  handleAcceptSeller: () => Promise<void>;
  // Pending ratings
  pendingRatings: PendingRating[];
  currentRatingIndex: number;
  handleRatingComplete: () => void;
  // Pending confirmations
  pendingConfirmation: PendingConfirmation | null;
  handleConfirmationResolved: () => void;
}

export function useOnboardingGates(): OnboardingGatesResult {
  const { user, loading: authLoading } = useAuth();
  const { isVerifiedSeller, loadingRole } = useRole();
  const { data: profile, isLoading: profileLoading } = useCurrentProfile(user?.id);
  const { data: sellerStatus, isLoading: sellerLoading } = useCurrentSellerStatus(user?.id);
  const { data: postLoginConfig } = useOnboardingConfig('post_login');
  const { data: onboardingEnabled, isLoading: enabledLoading } = useOnboardingEnabled();
  const queryClient = useQueryClient();
  
  // Start non-loading when there's no user — avoids flashing the spinner
  // on public routes like /welcome and /auth.
  const [loading, setLoading] = useState(() => !!user);
  const [activeModal, setActiveModal] = useState<OnboardingModal>('none');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Once we've completed the FIRST full check for a logged-in user, never
  // gate the UI behind a full-screen spinner again. Subsequent revalidations
  // (tab return, query refetch, profile updates) run silently in background.
  const [firstCheckDone, setFirstCheckDone] = useState(false);
  const [sellerPlanData, setSellerPlanData] = useState<SellerPlanData>({
    planLabel: '', creditsAmount: 0, boostAmount: 0, validityMonths: 12,
  });

  // Pending modals queue
  const [pendingModals, setPendingModals] = useState<OnboardingModal[]>([]);

  // Pending ratings state
  const [pendingRatings, setPendingRatings] = useState<PendingRating[]>([]);
  const [currentRatingIndex, setCurrentRatingIndex] = useState(0);

  // Pending confirmations state
  const [pendingConfirmations, setPendingConfirmations] = useState<PendingConfirmation[]>([]);

  // Safety timeout: if queries don't resolve in 10s, stop blocking
  useEffect(() => {
    if (!user || !loading) return;
    const timer = setTimeout(() => {
      console.warn('[OnboardingGates] Safety timeout reached, skipping onboarding checks');
      setLoading(false);
    }, 10000);
    return () => clearTimeout(timer);
  }, [user, loading]);

  // Campos primitivos derivados — evita re-rodar checkAll quando outros campos do profile mudam (ex.: avatar)
  const welcomeAcceptedAt = profile?.welcome_onboarding_accepted_at ?? null;
  const welcomeVersion = (profile as any)?.welcome_onboarding_version ?? 0;
  const studentLostPending = profile?.student_lost_pending ?? false;
  const sellerAcceptedAt = profile?.seller_onboarding_accepted_at ?? null;
  const isVerified = profile?.is_verified === true;
  const sellerLabel = sellerStatus?.sellerTypeLabel ?? '';
  const negotiationsPerYear = sellerStatus?.negotiationsPerYear ?? 0;
  const boostTotal = profile?.boost_plan_total ?? 0;
  const postLoginVersion = postLoginConfig?.version ?? 1;

  useEffect(() => {
    if (authLoading || loadingRole || profileLoading || sellerLoading || enabledLoading) return;
    if (!user) { setLoading(false); return; }
    // Only show the blocking loader on the very first check after login.
    // Revalidations must NOT flash a full-screen spinner — that's what made
    // tab-switching look like a page reload.
    if (!firstCheckDone) setLoading(true);

    let cancelled = false;

    async function checkAll() {
      // Kill-switch global: pula todos os onboardings se o admin desativou.
      // Pending ratings/confirmations continuam — são obrigações comerciais, não onboarding.
      const onboardingOn = onboardingEnabled !== false;

      // Welcome and studentLost come from cached profile — no extra queries!
      const needsWelcome = onboardingOn && checkWelcomeFromCache(
        welcomeAcceptedAt,
        welcomeVersion,
        postLoginVersion,
      );
      const needsStudentLost = onboardingOn && checkStudentLostFromCache(studentLostPending);

      // Seller onboarding from cached profile + seller status — no extra query!
      const sellerResult = onboardingOn && isVerifiedSeller
        ? checkSellerOnboardingFromCache(profile, sellerStatus)
        : { needsOnboarding: false, isVerified: false, planLabel: '', creditsAmount: 0, boostAmount: 0 };

      // Only ratings and confirmations need network calls
      const [ratingsResult, confirmationsResult] = await Promise.all([
        fetchPendingRatingsRPC().catch(() => [] as PendingRating[]),
        fetchPendingConfirmations().catch(() => [] as PendingConfirmation[]),
      ]);

      if (cancelled) return;

      const modals: OnboardingModal[] = [];

      // Onboarding modals first (priority order)
      if (needsWelcome) modals.push('welcome');
      if (needsStudentLost) modals.push('student-lost');
      if (sellerResult.needsOnboarding && sellerResult.isVerified) {
        modals.push('seller');
        setSellerPlanData({
          planLabel: sellerResult.planLabel || 'Vendedor',
          creditsAmount: sellerResult.creditsAmount || 0,
          boostAmount: sellerResult.boostAmount || 0,
          validityMonths: 12,
        });
      }

      // Then pending confirmations (higher priority than ratings)
      if (confirmationsResult.length > 0) {
        modals.push('pending-confirmation');
        setPendingConfirmations(confirmationsResult);
      }

      // Then pending ratings
      if (ratingsResult.length > 0) {
        modals.push('pending-rating');
        setPendingRatings(ratingsResult);
        setCurrentRatingIndex(0);
      }

      setPendingModals(modals);
      setActiveModal(modals[0] || 'none');
      setLoading(false);
      setFirstCheckDone(true);
    }

    checkAll();
    return () => { cancelled = true; };
  }, [
    user, authLoading, loadingRole, profileLoading, sellerLoading, enabledLoading,
    onboardingEnabled, isVerifiedSeller, postLoginVersion,
    welcomeAcceptedAt, welcomeVersion, studentLostPending,
    sellerAcceptedAt, isVerified, sellerLabel, negotiationsPerYear, boostTotal,
  ]);

  const advanceToNext = () => {
    setPendingModals(prev => {
      const next = prev.slice(1);
      setActiveModal(next[0] || 'none');
      return next;
    });
  };

  const handleAcceptWelcome = async () => {
    if (!user) return;
    setIsSubmitting(true);
    try {
      // Garante que estamos gravando contra a versão real (evita race quando o config ainda não carregou).
      let version = postLoginConfig?.version;
      if (version == null) {
        const fresh = await fetchOnboardingConfig('post_login').catch(() => null);
        version = fresh?.version ?? 1;
      }
      const success = await acceptWelcomeOnboarding(user.id, queryClient, version);
      if (success) {
        advanceToNext();
      } else {
        toast.error('Não foi possível concluir agora. Tente novamente em alguns instantes.');
      }
    } catch (error) {
      console.error('[OnboardingGates] Failed to accept welcome onboarding:', error);
      toast.error('Não foi possível concluir agora. Tente novamente em alguns instantes.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAcknowledgeStudentLost = async () => {
    if (!user) return;
    setIsSubmitting(true);
    const success = await acknowledgeStudentLost(user.id, queryClient);
    if (success) {
      const displayName = profile?.display_name || user.email || null;
      logActivity('student_lost_acknowledged', user.id, displayName);
      advanceToNext();
    }
    setIsSubmitting(false);
  };

  const handleAcceptSeller = async () => {
    if (!user) return;
    setIsSubmitting(true);
    const success = await acceptSellerOnboarding(user.id, queryClient);
    if (success) advanceToNext();
    setIsSubmitting(false);
  };

  const handleRatingComplete = () => {
    if (currentRatingIndex < pendingRatings.length - 1) {
      setCurrentRatingIndex(prev => prev + 1);
    } else {
      // All ratings done, advance to next modal type
      advanceToNext();
    }
  };

  const handleConfirmationResolved = () => {
    const remaining = pendingConfirmations.slice(1);
    if (remaining.length > 0) {
      setPendingConfirmations(remaining);
    } else {
      advanceToNext();
    }
  };

  return {
    loading: loading && !!user,
    activeModal,
    sellerPlanData,
    isSubmitting,
    handleAcceptWelcome,
    handleAcknowledgeStudentLost,
    handleAcceptSeller,
    pendingRatings,
    currentRatingIndex,
    handleRatingComplete,
    pendingConfirmation: pendingConfirmations[0] || null,
    handleConfirmationResolved,
  };
}
